import * as i0 from '@angular/core';
import { ElementRef, Directive, Inject, HostListener, EventEmitter, Component, ChangeDetectionStrategy, Optional, Self, ViewChildren, ViewChild, Input, HostBinding, Output, NgModule } from '@angular/core';
import { defaultEditorTools } from '@taiga-ui/addon-editor/constants';
import { TuiTiptapEditorService } from '@taiga-ui/addon-editor/directives';
import { TuiEditorTool } from '@taiga-ui/addon-editor/enums';
import { TUI_IMAGE_LOADER, TUI_ATTACH_FILES_OPTIONS, TUI_ATTACH_FILES_LOADER, TUI_EDITOR_TOOLBAR_TEXTS, TUI_EDITOR_OPTIONS } from '@taiga-ui/addon-editor/tokens';
import * as i5 from '@taiga-ui/cdk';
import { tuiIsNativeFocusedIn, tuiClamp, tuiIsNativeMouseFocusable, tuiGetClosestFocusable, EMPTY_QUERY, tuiAssert, TuiDestroyService, TuiFocusableModule, TuiItemModule, TuiItemDirective } from '@taiga-ui/cdk';
import { take, takeUntil } from 'rxjs/operators';
import * as i1 from '@taiga-ui/core';
import { TuiHintModule, TuiButtonModule, TuiHostedDropdownModule } from '@taiga-ui/core';
import * as i2 from '@taiga-ui/addon-editor/components/toolbar-tools';
import { TuiTableMergeCellsModule, TuiTableCellColorModule, TuiTableRowColumnManagerModule, TuiTableCreateModule, TuiFontSizeModule, TuiFontStyleModule, TuiAlignContentModule, TuiListConfigsModule, TuiTextColorModule, TuiHighlightColorModule, TuiCodeModule, TuiDetailsModule, TuiEditorToolGroup } from '@taiga-ui/addon-editor/components/toolbar-tools';
import * as i3 from '@taiga-ui/addon-editor/components/edit-link';
import { TuiEditLinkModule } from '@taiga-ui/addon-editor/components/edit-link';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i7 from '@taiga-ui/addon-editor/abstract';
import * as i8 from 'rxjs';

class TuiToolbarNavigationManagerDirective {
    constructor(el) {
        this.el = el;
    }
    get toolsContainers() {
        return Array.from(this.el.nativeElement.querySelectorAll('[tuiItem]'));
    }
    onHorizontalNavigation(toPrevious) {
        const { toolsContainers } = this;
        const focusedToolIndex = toolsContainers.findIndex(tuiIsNativeFocusedIn);
        const targetToolIndex = tuiClamp(focusedToolIndex + (toPrevious ? -1 : 1), 0, toolsContainers.length - 1);
        const targetToolWrapper = toolsContainers[targetToolIndex];
        const targetTool = toPrevious
            ? this.findPreviousTool(targetToolWrapper)
            : this.findNextTool(targetToolWrapper);
        if (targetTool) {
            targetTool.focus();
        }
    }
    findFirstFocusableTool(reversed = false) {
        const tools = reversed
            ? this.toolsContainers.slice().reverse()
            : this.toolsContainers;
        for (const el of tools) {
            const focusableElement = tuiIsNativeMouseFocusable(el)
                ? el
                : tuiGetClosestFocusable({ initial: el, root: el, keyboard: false });
            if (focusableElement) {
                return focusableElement;
            }
        }
        return null;
    }
    findPreviousTool(wrapper) {
        if (tuiIsNativeMouseFocusable(wrapper)) {
            return wrapper;
        }
        const lookedInside = tuiGetClosestFocusable({
            initial: wrapper,
            root: wrapper,
            keyboard: false,
        });
        return (lookedInside ||
            tuiGetClosestFocusable({
                initial: wrapper,
                root: this.el.nativeElement,
                previous: true,
                keyboard: false,
            }));
    }
    findNextTool(wrapper) {
        return tuiIsNativeMouseFocusable(wrapper)
            ? wrapper
            : tuiGetClosestFocusable({
                initial: wrapper,
                root: this.el.nativeElement,
                keyboard: false,
            });
    }
}
TuiToolbarNavigationManagerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarNavigationManagerDirective, deps: [{ token: ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
TuiToolbarNavigationManagerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: TuiToolbarNavigationManagerDirective, selector: "[tuiToolbarNavigationManager]", host: { listeners: { "keydown.arrowRight.prevent": "onHorizontalNavigation(false)", "keydown.arrowLeft.prevent": "onHorizontalNavigation(true)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarNavigationManagerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tuiToolbarNavigationManager]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }]; }, propDecorators: { onHorizontalNavigation: [{
                type: HostListener,
                args: ['keydown.arrowRight.prevent', ['false']]
            }, {
                type: HostListener,
                args: ['keydown.arrowLeft.prevent', ['true']]
            }] } });

class TuiToolbarComponent {
    constructor(el, editor, imageLoader, attachOptions, filesLoader, texts$, defaultOptions, destroy$) {
        this.el = el;
        this.editor = editor;
        this.imageLoader = imageLoader;
        this.attachOptions = attachOptions;
        this.filesLoader = filesLoader;
        this.texts$ = texts$;
        this.defaultOptions = defaultOptions;
        this.destroy$ = destroy$;
        this.dropdowns = EMPTY_QUERY;
        this.colors = this.defaultOptions.colors;
        this.disabled = false;
        this.linkAdded = new EventEmitter();
        this.texClicked = new EventEmitter();
        this.fileAttached = new EventEmitter();
        this.TuiEditorTool = TuiEditorTool;
        this.toolsSet = new Set(defaultEditorTools);
    }
    set tools(value) {
        this.toolsSet = new Set(value);
    }
    get focused() {
        return (tuiIsNativeFocusedIn(this.el.nativeElement) ||
            !!this.dropdowns.find(({ nativeElement }) => tuiIsNativeFocusedIn(nativeElement)));
    }
    get focusable() {
        return !this.focused && !this.disabled;
    }
    get unorderedList() {
        return this.editor.isActive('bulletList');
    }
    get orderedList() {
        return this.editor.isActive('orderedList');
    }
    get blockquote() {
        return this.editor.isActive('blockquote');
    }
    get a() {
        return this.editor.isActive('link');
    }
    get jumpAnchor() {
        return this.editor.isActive('jumpAnchor');
    }
    get canOpenAnchor() {
        return !this.a && !this.jumpAnchor;
    }
    get undoDisabled() {
        return this.editor.undoDisabled();
    }
    get redoDisabled() {
        return this.editor.redoDisabled();
    }
    get subscript() {
        return this.editor.isActive('subscript');
    }
    get superscript() {
        return this.editor.isActive('superscript');
    }
    get formatEnabled() {
        return (this.enabled(TuiEditorTool.Bold) ||
            this.enabled(TuiEditorTool.Italic) ||
            this.enabled(TuiEditorTool.Underline) ||
            this.enabled(TuiEditorTool.Strikethrough));
    }
    get firstBigBlockEnabled() {
        return (this.formatEnabled ||
            this.enabled(TuiEditorTool.Align) ||
            this.enabled(TuiEditorTool.List) ||
            this.enabled(TuiEditorTool.Quote) ||
            this.enabled(TuiEditorTool.Link) ||
            this.enabled(TuiEditorTool.Anchor) ||
            this.enabled(TuiEditorTool.Attach));
    }
    get secondBigBlockEnabled() {
        return (this.enabled(TuiEditorTool.Code) ||
            this.enabled(TuiEditorTool.Tex) ||
            this.enabled(TuiEditorTool.Img) ||
            this.enabled(TuiEditorTool.HR));
    }
    onMouseDown(event, target) {
        if (target.closest('button')) {
            return;
        }
        event.preventDefault();
        this.editor.focus();
    }
    onBottomFocus() {
        this.focusLast();
    }
    onTopFocus() {
        this.focusFirst();
    }
    onImage(input) {
        var _a;
        const file = (_a = input.files) === null || _a === void 0 ? void 0 : _a[0];
        input.value = '';
        if (!file) {
            return;
        }
        this.imageLoader(file)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(image => this.addImage(image));
    }
    onAttach(input) {
        var _a;
        const files = Array.from(input.files || []);
        input.value = '';
        if (!files) {
            return;
        }
        ngDevMode &&
            tuiAssert.assert(!!this.filesLoader, 'Please provide TUI_ATTACH_FILES_LOADER');
        (_a = this.filesLoader) === null || _a === void 0 ? void 0 : _a.call(this, files).pipe(take(1), takeUntil(this.destroy$)).subscribe(attachedFiles => this.fileAttached.emit(attachedFiles));
    }
    onTeX() {
        this.texClicked.emit();
    }
    onLink(hosted, url) {
        hosted.open = false;
        if (url) {
            this.editor.toggleLink(url);
        }
    }
    setAnchor(hosted, anchor) {
        hosted.open = false;
        if (anchor) {
            this.editor.setAnchor(anchor);
            this.editor.selectClosest();
        }
    }
    removeAnchor() {
        this.editor.removeAnchor();
    }
    enabled(tool) {
        return this.toolsSet.has(tool);
    }
    undo() {
        this.editor.undo();
    }
    redo() {
        this.editor.redo();
    }
    insertHorizontalRule() {
        this.editor.setHorizontalRule();
    }
    removeFormat() {
        this.editor.removeFormat();
    }
    toggleOrderedList() {
        this.editor.toggleOrderedList();
    }
    toggleQuote() {
        this.editor.toggleBlockquote();
    }
    toggleSubscript() {
        this.editor.toggleSubscript();
    }
    toggleSuperscript() {
        this.editor.toggleSuperscript();
    }
    addImage(image) {
        this.editor.setImage(image);
    }
    focusFirst() {
        var _a;
        const firstButton = (_a = this.navigationManager) === null || _a === void 0 ? void 0 : _a.findFirstFocusableTool();
        if (firstButton) {
            firstButton.focus();
        }
    }
    focusLast() {
        var _a;
        const lastButton = (_a = this.navigationManager) === null || _a === void 0 ? void 0 : _a.findFirstFocusableTool(true);
        if (lastButton) {
            lastButton.focus();
        }
    }
}
TuiToolbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarComponent, deps: [{ token: ElementRef, optional: true }, { token: TuiTiptapEditorService }, { token: TUI_IMAGE_LOADER }, { token: TUI_ATTACH_FILES_OPTIONS }, { token: TUI_ATTACH_FILES_LOADER, optional: true }, { token: TUI_EDITOR_TOOLBAR_TEXTS }, { token: TUI_EDITOR_OPTIONS }, { token: TuiDestroyService, self: true }], target: i0.ɵɵFactoryTarget.Component });
TuiToolbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiToolbarComponent, selector: "tui-toolbar", inputs: { colors: "colors", disabled: "disabled", tools: "tools" }, outputs: { linkAdded: "linkAdded", texClicked: "texClicked", fileAttached: "fileAttached" }, host: { attributes: { "role": "toolbar" }, listeners: { "mousedown": "onMouseDown($event,$event.target)" }, properties: { "class._disabled": "this.disabled" } }, providers: [TuiDestroyService], viewQueries: [{ propertyName: "navigationManager", first: true, predicate: TuiToolbarNavigationManagerDirective, descendants: true }, { propertyName: "dropdowns", predicate: ["dropdown"], descendants: true, read: ElementRef }], ngImport: i0, template: "<button\n    type=\"button\"\n    class=\"t-prevent-parent-label-behavior\"\n>\n    <!-- @note: this is a html huck to avoid autofocus, if the parent element is a label  -->\n</button>\n<ng-container *ngIf=\"editor.stateChange$ | async\"></ng-container>\n<div\n    [tuiFocusable]=\"focusable\"\n    (focus)=\"onTopFocus()\"\n></div>\n<div\n    [tuiFocusable]=\"focusable\"\n    (focus)=\"onBottomFocus()\"\n></div>\n<!-- TODO: Change to `tuiHintDescribe` when figure tuiDriver order issue -->\n<section\n    *ngIf=\"texts$ | async as texts\"\n    tuiToolbarNavigationManager\n    class=\"t-tools-wrapper\"\n>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Undo)\"\n        class=\"t-block\"\n    >\n        <button\n            tuiItem\n            tuiIconButton\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconUndoLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.undo\"\n            [disabled]=\"undoDisabled\"\n            [focusable]=\"false\"\n            (click)=\"undo()\"\n        ></button>\n        <button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconRedoLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.redo\"\n            [disabled]=\"redoDisabled\"\n            [focusable]=\"false\"\n            (click)=\"redo()\"\n        ></button>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Size)\"\n        class=\"t-block\"\n    >\n        <tui-font-size\n            tuiItem\n            class=\"t-tool t-wrapper\"\n        ></tui-font-size>\n    </div>\n    <div\n        *ngIf=\"firstBigBlockEnabled\"\n        class=\"t-block\"\n    >\n        <tui-font-style\n            *ngIf=\"formatEnabled\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n            [enabledTools]=\"toolsSet\"\n        ></tui-font-style>\n\n        <tui-align-content\n            *ngIf=\"enabled(TuiEditorTool.Align)\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n        ></tui-align-content>\n\n        <tui-list-configs\n            *ngIf=\"enabled(TuiEditorTool.List)\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n        ></tui-list-configs>\n\n        <button\n            *ngIf=\"enabled(TuiEditorTool.Quote)\"\n            #button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconQuoteLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.quote\"\n            [pseudoActive]=\"blockquote\"\n            [disabled]=\"unorderedList || orderedList\"\n            [focusable]=\"false\"\n            (click)=\"toggleQuote()\"\n        ></button>\n        <tui-hosted-dropdown\n            *ngIf=\"enabled(TuiEditorTool.Link)\"\n            #link\n            tuiItem\n            class=\"t-wrapper\"\n            [canOpen]=\"canOpenAnchor\"\n            [content]=\"linkDropdown\"\n        >\n            <button\n                #button\n                tuiIconButton\n                type=\"button\"\n                size=\"s\"\n                icon=\"tuiIconLinkLarge\"\n                appearance=\"icon\"\n                tuiHintDirection=\"top-left\"\n                tuiHintDescribe\n                class=\"t-tool\"\n                [tuiHint]=\"texts.link\"\n                [pseudoActive]=\"link.open || a\"\n                [focusable]=\"link.open\"\n            ></button>\n            <ng-template #linkDropdown>\n                <tui-edit-link\n                    #dropdown\n                    (addLink)=\"onLink(link, $event)\"\n                    (removeLink)=\"onLink(link)\"\n                ></tui-edit-link>\n            </ng-template>\n        </tui-hosted-dropdown>\n        <tui-hosted-dropdown\n            *ngIf=\"enabled(TuiEditorTool.Anchor)\"\n            #anchor\n            class=\"t-wrapper\"\n            [canOpen]=\"canOpenAnchor\"\n            [content]=\"anchorDropdown\"\n        >\n            <button\n                tuiIconButton\n                type=\"button\"\n                size=\"s\"\n                icon=\"tuiIconAnchorLarge\"\n                tuiHintDirection=\"top-left\"\n                appearance=\"icon\"\n                tuiHintDescribe\n                [focusable]=\"false\"\n                [pseudoActive]=\"anchor.open || jumpAnchor\"\n                [tuiHint]=\"texts.insertAnchor\"\n            ></button>\n            <ng-template #anchorDropdown>\n                <tui-edit-link\n                    [anchorMode]=\"true\"\n                    (addLink)=\"setAnchor(anchor, $event)\"\n                    (removeLink)=\"removeAnchor()\"\n                ></tui-edit-link>\n            </ng-template>\n        </tui-hosted-dropdown>\n        <ng-container *ngIf=\"enabled(TuiEditorTool.Attach)\">\n            <button\n                #button\n                tuiIconButton\n                tuiItem\n                type=\"button\"\n                size=\"s\"\n                icon=\"tuiIconPaperclipLarge\"\n                appearance=\"icon\"\n                tuiHintDirection=\"top-left\"\n                tuiHintDescribe\n                class=\"t-tool t-tool_margin\"\n                [tuiHint]=\"texts.attach\"\n                [focusable]=\"false\"\n                (click)=\"fileUpload.click()\"\n            ></button>\n\n            <input\n                #fileUpload\n                type=\"file\"\n                class=\"t-file-upload\"\n                [accept]=\"attachOptions.accept\"\n                [multiple]=\"attachOptions.multiple\"\n                [tuiFocusable]=\"false\"\n                (change)=\"onAttach(fileUpload)\"\n            />\n        </ng-container>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Sub) || enabled(TuiEditorTool.Sup)\"\n        class=\"t-block\"\n    >\n        <button\n            *ngIf=\"enabled(TuiEditorTool.Sub)\"\n            #button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconSubscriptLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.subscript\"\n            [pseudoActive]=\"subscript\"\n            [focusable]=\"false\"\n            (click)=\"toggleSubscript()\"\n        ></button>\n        <button\n            *ngIf=\"enabled(TuiEditorTool.Sup)\"\n            #button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconSuperscriptLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.superscript\"\n            [pseudoActive]=\"superscript\"\n            [focusable]=\"false\"\n            (click)=\"toggleSuperscript()\"\n        ></button>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Color) || enabled(TuiEditorTool.Hilite)\"\n        class=\"t-block\"\n    >\n        <tui-text-color\n            *ngIf=\"enabled(TuiEditorTool.Color)\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n            [colors]=\"colors\"\n        ></tui-text-color>\n\n        <tui-highlight-color\n            *ngIf=\"enabled(TuiEditorTool.Hilite)\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n            [colors]=\"colors\"\n        ></tui-highlight-color>\n    </div>\n    <div\n        *ngIf=\"secondBigBlockEnabled\"\n        class=\"t-block\"\n    >\n        <tui-code\n            *ngIf=\"enabled(TuiEditorTool.Code)\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n        ></tui-code>\n\n        <button\n            *ngIf=\"enabled(TuiEditorTool.Tex)\"\n            #button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconTeXLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool\"\n            [tuiHint]=\"texts.tex\"\n            [focusable]=\"false\"\n            (click)=\"onTeX()\"\n        ></button>\n        <div\n            *ngIf=\"enabled(TuiEditorTool.Img)\"\n            tuiItem\n            class=\"t-wrapper\"\n        >\n            <button\n                #button\n                tuiIconButton\n                type=\"button\"\n                size=\"s\"\n                icon=\"tuiIconImageLarge\"\n                appearance=\"icon\"\n                tuiHintDirection=\"top-left\"\n                tuiHintDescribe\n                class=\"t-tool\"\n                [tuiHint]=\"texts.image\"\n                [focusable]=\"false\"\n                (click)=\"image.click()\"\n            ></button>\n\n            <input\n                #image\n                type=\"file\"\n                accept=\"image/*\"\n                class=\"t-image\"\n                [tuiFocusable]=\"false\"\n                (change)=\"onImage(image)\"\n            />\n        </div>\n        <button\n            *ngIf=\"enabled(TuiEditorTool.HR)\"\n            #button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconHrLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.insertHorizontalRule\"\n            [focusable]=\"false\"\n            (click)=\"insertHorizontalRule()\"\n        ></button>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Clear)\"\n        class=\"t-block\"\n    >\n        <button\n            #button\n            tuiIconButton\n            tuiItem\n            type=\"button\"\n            size=\"s\"\n            icon=\"tuiIconClearFormatLarge\"\n            appearance=\"icon\"\n            tuiHintDirection=\"top-left\"\n            tuiHintDescribe\n            class=\"t-tool t-tool_margin\"\n            [tuiHint]=\"texts.clear\"\n            [focusable]=\"false\"\n            (click)=\"removeFormat()\"\n        ></button>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Details)\"\n        class=\"t-block\"\n    >\n        <tui-details\n            tuiItem\n            class=\"t-tool t-tool_margin\"\n        ></tui-details>\n\n        <tui-details-remove\n            tuiItem\n            class=\"t-tool t-tool_margin\"\n        ></tui-details-remove>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Group)\"\n        class=\"t-block\"\n    >\n        <tui-editor-group-tool\n            tuiItem\n            class=\"t-tool t-tool_margin\"\n        ></tui-editor-group-tool>\n        <tui-table-cell-color\n            *ngIf=\"!enabled(TuiEditorTool.Table)\"\n            tuiItem\n            class=\"t-tool t-wrapper\"\n            [colors]=\"colors\"\n        ></tui-table-cell-color>\n    </div>\n    <div\n        *ngIf=\"enabled(TuiEditorTool.Table)\"\n        class=\"t-block\"\n    >\n        <tui-table-create\n            tuiItem\n            class=\"t-tool t-tool_margin\"\n        ></tui-table-create>\n\n        <tui-table-row-column-manager\n            tuiItem\n            class=\"t-tool t-tool_margin\"\n        ></tui-table-row-column-manager>\n\n        <tui-table-merge-cells\n            tuiItem\n            class=\"t-tool t-tool_margin\"\n        ></tui-table-merge-cells>\n\n        <tui-table-cell-color\n            tuiItem\n            class=\"t-tool t-wrapper\"\n            [colors]=\"colors\"\n        ></tui-table-cell-color>\n    </div>\n    <div class=\"t-block\">\n        <ng-content></ng-content>\n    </div>\n</section>\n", styles: [".t-tools-wrapper{scrollbar-width:none;-ms-overflow-style:none;display:flex;padding:.25rem .75rem;overflow:auto;flex-wrap:wrap;box-shadow:0 .125rem .1875rem #0000001a}.t-tools-wrapper::-webkit-scrollbar,.t-tools-wrapper::-webkit-scrollbar-thumb{background:transparent;width:0;height:0}.t-tools-wrapper._disabled{pointer-events:none;opacity:var(--tui-disabled-opacity)}.t-block{display:flex;padding:.25rem 0}.t-block:not(:last-child):after{content:\"\";display:block;margin:0 .25rem;width:1px;background:var(--tui-secondary-active)}.t-tool{transition-property:background;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out}button.t-tool:hover,.t-tool ::ng-deep button:hover{background:var(--tui-secondary-hover)}.t-tool_margin{margin:0 .25rem}.t-dropdown{padding:.75rem}.t-wrapper{position:relative;margin:0 .25rem}.t-plate{position:absolute;top:1.375rem;left:.25rem;width:1.5rem;height:.25rem;pointer-events:none}.t-plate_paint{left:.375rem;width:1.25rem}.t-font{padding:.3em 0;outline:none;width:100%;pointer-events:none}.t-image{position:absolute;top:0;left:0;width:100%;height:100%;font-size:0;opacity:0;cursor:pointer}.t-image::-webkit-file-upload-button{display:none}.t-file-upload{display:none}.t-color-picker ::ng-deep tui-hosted-dropdown,.t-color-picker ::ng-deep tui-color-picker,.t-color-picker ::ng-deep tui-color-edit{display:none}.t-prevent-parent-label-behavior{display:none}\n"], components: [{ type: i1.TuiButtonComponent, selector: "button[tuiButton], button[tuiIconButton], a[tuiButton], a[tuiIconButton]", inputs: ["appearance", "disabled", "icon", "iconRight", "shape", "showLoader", "size"] }, { type: i2.TuiFontSizeComponent, selector: "tui-font-size" }, { type: i2.TuiFontStyleComponent, selector: "tui-font-style", inputs: ["enabledTools"] }, { type: i2.TuiAlignContentComponent, selector: "tui-align-content" }, { type: i2.TuiListConfigsComponent, selector: "tui-list-configs" }, { type: i1.TuiHostedDropdownComponent, selector: "tui-hosted-dropdown", inputs: ["content", "sided", "canOpen", "open"], outputs: ["openChange", "focusedChange"] }, { type: i3.TuiEditLinkComponent, selector: "tui-edit-link", inputs: ["anchorMode"], outputs: ["addLink", "removeLink"] }, { type: i2.TuiTextColorComponent, selector: "tui-text-color", inputs: ["colors"] }, { type: i2.TuiHighlightColorComponent, selector: "tui-highlight-color", inputs: ["colors"] }, { type: i2.TuiCodeComponent, selector: "tui-code" }, { type: i2.TuiDetailsComponent, selector: "tui-details" }, { type: i2.TuiDetailsRemoveComponent, selector: "tui-details-remove" }, { type: i2.TuiEditorGroupToolComponent, selector: "tui-editor-group-tool" }, { type: i2.TuiTableCellColorComponent, selector: "tui-table-cell-color", inputs: ["colors"] }, { type: i2.TuiTableCreateComponent, selector: "tui-table-create" }, { type: i2.TuiTableRowColumnManagerComponent, selector: "tui-table-row-column-manager" }, { type: i2.TuiTableMergeCellsComponent, selector: "tui-table-merge-cells" }], directives: [{ type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.TuiFocusableDirective, selector: "[tuiFocusable]", inputs: ["tuiFocusable"] }, { type: TuiToolbarNavigationManagerDirective, selector: "[tuiToolbarNavigationManager]" }, { type: i5.TuiItemDirective, selector: "[tuiItem]" }, { type: i1.TuiHintDescribeDirective, selector: "[tuiHintDescribe]", inputs: ["tuiHintDescribe"] }, { type: i1.TuiHintDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHint", "tuiHintContext", "tuiHintAppearance"] }, { type: i1.TuiHintDriverDirective, selector: "[tuiHint]" }, { type: i1.TuiHintHoverDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHintShowDelay", "tuiHintHideDelay"], exportAs: ["tuiHintHover"] }, { type: i1.TuiHintPositionDirective, selector: "[tuiHint]:not([tuiHintCustomPosition])", inputs: ["tuiHintDirection"] }], pipes: { "async": i4.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-toolbar',
                    templateUrl: './toolbar.template.html',
                    styleUrls: ['./toolbar.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [TuiDestroyService],
                    host: {
                        role: 'toolbar',
                    },
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ElementRef]
                }] }, { type: i7.AbstractTuiEditor, decorators: [{
                    type: Inject,
                    args: [TuiTiptapEditorService]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_IMAGE_LOADER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_ATTACH_FILES_OPTIONS]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TUI_ATTACH_FILES_LOADER]
                }] }, { type: i8.Observable, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_TOOLBAR_TEXTS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_OPTIONS]
                }] }, { type: i5.TuiDestroyService, decorators: [{
                    type: Self
                }, {
                    type: Inject,
                    args: [TuiDestroyService]
                }] }]; }, propDecorators: { dropdowns: [{
                type: ViewChildren,
                args: ['dropdown', { read: ElementRef }]
            }], navigationManager: [{
                type: ViewChild,
                args: [TuiToolbarNavigationManagerDirective]
            }], colors: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class._disabled']
            }], linkAdded: [{
                type: Output
            }], texClicked: [{
                type: Output
            }], fileAttached: [{
                type: Output
            }], tools: [{
                type: Input
            }], onMouseDown: [{
                type: HostListener,
                args: ['mousedown', ['$event', '$event.target']]
            }] } });

class TuiToolbarModule {
}
TuiToolbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiToolbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarModule, declarations: [TuiToolbarComponent, TuiToolbarNavigationManagerDirective], imports: [CommonModule,
        TuiHintModule,
        TuiFocusableModule,
        TuiButtonModule,
        TuiHostedDropdownModule,
        TuiEditLinkModule,
        TuiTableMergeCellsModule,
        TuiTableCellColorModule,
        TuiTableRowColumnManagerModule,
        TuiTableCreateModule,
        TuiFontSizeModule,
        TuiFontStyleModule,
        TuiAlignContentModule,
        TuiListConfigsModule,
        TuiTextColorModule,
        TuiHighlightColorModule,
        TuiCodeModule,
        TuiDetailsModule,
        TuiItemModule,
        TuiEditorToolGroup], exports: [TuiToolbarComponent, TuiItemDirective] });
TuiToolbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarModule, imports: [[
            CommonModule,
            TuiHintModule,
            TuiFocusableModule,
            TuiButtonModule,
            TuiHostedDropdownModule,
            TuiEditLinkModule,
            TuiTableMergeCellsModule,
            TuiTableCellColorModule,
            TuiTableRowColumnManagerModule,
            TuiTableCreateModule,
            TuiFontSizeModule,
            TuiFontStyleModule,
            TuiAlignContentModule,
            TuiListConfigsModule,
            TuiTextColorModule,
            TuiHighlightColorModule,
            TuiCodeModule,
            TuiDetailsModule,
            TuiItemModule,
            TuiEditorToolGroup,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiToolbarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TuiHintModule,
                        TuiFocusableModule,
                        TuiButtonModule,
                        TuiHostedDropdownModule,
                        TuiEditLinkModule,
                        TuiTableMergeCellsModule,
                        TuiTableCellColorModule,
                        TuiTableRowColumnManagerModule,
                        TuiTableCreateModule,
                        TuiFontSizeModule,
                        TuiFontStyleModule,
                        TuiAlignContentModule,
                        TuiListConfigsModule,
                        TuiTextColorModule,
                        TuiHighlightColorModule,
                        TuiCodeModule,
                        TuiDetailsModule,
                        TuiItemModule,
                        TuiEditorToolGroup,
                    ],
                    declarations: [TuiToolbarComponent, TuiToolbarNavigationManagerDirective],
                    exports: [TuiToolbarComponent, TuiItemDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiToolbarComponent, TuiToolbarModule, TuiToolbarNavigationManagerDirective };
